import {
  Button,
  Content,
  FillIcon,
  Flex,
  fromTheme,
  StrokeIcon,
  usePlatformInfo,
  usePlatformProps,
} from '@acsbe/ui';
import { upperFirst } from 'lodash-es';
import { styled } from 'styled-components';
import {
  useBilling,
  useDomainSelection,
  useMerchantSession,
} from '../../../context';
import { FlyoutActionMenu } from '../../dashboard/flyout-action-menu';
import { ChoosePlanButton } from './choose-plan-button';
import { LicenseDetails } from './types';

interface PlanBarProps {
  licenseDetails?: LicenseDetails;
  isWidgetEnabled: boolean;
}

export const PlanBar = (props: PlanBarProps) => {
  const { accountId, licenseId } = useDomainSelection();
  const { wooPendingPayment } = useBilling();
  const statement = () => {
    window.open(
      `${import.meta.env.VITE_DASHBOARD_URL}/app/statement?license=${licenseId}`,
      '_blank',
    );
  };

  const p = usePlatformProps();
  const { isPhablet, isTablet, isMobile, uptoPhablet } = usePlatformInfo();

  const PlanWarningBanner = () => {
    const calculateExpiryDateStr = (daysLeft: number | string | undefined) => {
      if (!daysLeft) return '';
      daysLeft = Number(daysLeft);
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() + daysLeft);
      const options = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      } as const;
      return currentDate.toLocaleDateString('en-US', options);
    };

    const parseExpiryDateStr = (expiryDateStr: string | undefined) => {
      if (!expiryDateStr) return '';
      const expiryDate = new Date(expiryDateStr);
      const options = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      } as const;
      return expiryDate.toLocaleDateString('en-US', options);
    };

    if (wooPendingPayment === null) {
      return null;
    } else if (wooPendingPayment) {
      return (
        <Flex
          align="flex-start"
          gap={2}
          row={!isPhablet && !isMobile}
          justify="space-between"
          padding={2}
          width="100%"
          boxType="border"
          style={{ backgroundColor: '#FFF5D8', borderRadius: '8px' }}
        >
          <Flex row align="center" gap={2}>
            {!uptoPhablet && (
              <FillIcon name="Warning" size={24} style={{ color: '#9D7700' }} />
            )}
            <Content>
              <b>We're processing your payment!</b> Please don't close this
              window until it's complete.
            </Content>
          </Flex>
        </Flex>
      );
    } else if (props.licenseDetails?.status === 'trial') {
      return (
        <Flex
          align="flex-start"
          gap={2}
          row={!isPhablet && !isMobile}
          justify="space-between"
          padding={2}
          width="100%"
          boxType="border"
          style={{ backgroundColor: '#FFF5D8', borderRadius: '8px' }}
        >
          <Flex row align="center" gap={2}>
            {!uptoPhablet && (
              <FillIcon name="Warning" size={24} style={{ color: '#9D7700' }} />
            )}
            <Content>
              <b>
                Your trial ends on{' '}
                {calculateExpiryDateStr(props.licenseDetails?.timeLeft)}.
              </b>{' '}
              Choose a plan to keep your store accessible when the trial period
              ends.
            </Content>
          </Flex>
          <ChoosePlanButton />
        </Flex>
      );
    } else if (props.licenseDetails?.status === 'expired') {
      if (props.licenseDetails?.plan?.name === null) {
        return (
          <Flex
            align="flex-start"
            gap={2}
            row={!isPhablet && !isMobile}
            justify="space-between"
            padding={2}
            width="100%"
            boxType="border"
            style={{ backgroundColor: '#FDE8E9', borderRadius: '8px' }}
          >
            <Flex row align="center" gap={2}>
              {!uptoPhablet && (
                <FillIcon
                  name="Warning"
                  size={24}
                  style={{ color: '#D8161F' }}
                />
              )}
              <Content>
                <b>Your trial has expired!</b> Choose a plan to help keep your
                store inclusive and your business mitigate legal risk.
              </Content>
            </Flex>
            <ChoosePlanButton />
          </Flex>
        );
      } else {
        return (
          <Flex
            align="flex-start"
            gap={2}
            row={!isPhablet && !isMobile}
            justify="space-between"
            padding={2}
            width="100%"
            boxType="border"
            style={{ backgroundColor: '#FDE8E9', borderRadius: '8px' }}
          >
            <Flex row align="center" gap={2}>
              {!uptoPhablet && (
                <FillIcon
                  name="Warning"
                  size={24}
                  style={{ color: '#D8161F' }}
                />
              )}
              <Content>
                <b>Your plan has expired!</b> Choose a plan to help keep your
                store inclusive and your business mitigate legal risk.
              </Content>
            </Flex>
            <ChoosePlanButton />
          </Flex>
        );
      }
    }
    return null;
  };
  const PlanComponent = () => {
    return (
      <>
        {
          <Flex row align="center" gap={2} self="stretch" height="32px">
            <Flex row align="flex-start" gap={2}>
              <StrokeIcon name="Flag" size={16} />
              <Content scale="accent" emphasis="low" lineHeight={1.25}>
                Plan:
              </Content>
              <Content scale="base" emphasis="none" lineHeight={1.25}>
                {props.licenseDetails?.status === 'trial'
                  ? 'Trial'
                  : props.licenseDetails?.status === 'expired' &&
                      props.licenseDetails?.plan?.name === null
                    ? 'No active plan'
                    : props.licenseDetails?.plan?.name !== null
                      ? upperFirst(props.licenseDetails?.plan?.name)
                      : 'Trial'}
                {props.licenseDetails?.plan?.name
                  ? props.licenseDetails?.monthly
                    ? ' - Monthly'
                    : ' - Yearly'
                  : ''}
              </Content>
            </Flex>
          </Flex>
        }
      </>
    );
  };

  const displayStatus = () => {
    if (wooPendingPayment) {
      return 'Pending payment confirmation';
    } else if (props.licenseDetails?.status === 'trial') {
      return 'Active trial';
    } else if (props.licenseDetails?.status === 'cancelled') {
      return 'Scheduled Cancellation';
    } else {
      return upperFirst(props.licenseDetails?.status || '');
    }
  };

  const StatusComponent = () => {
    const calculateExpiryDateStr = (daysLeft: number | string | undefined) => {
      if (!daysLeft) return '';
      daysLeft = Number(daysLeft);
      if (props?.licenseDetails?.datePlanExpires) {
        daysLeft = daysLeft + 1; // to account for the expiry date being inclusive
      }
      if (
        props?.licenseDetails?.datePlanExpires &&
        props.licenseDetails?.shouldCancelNextCycle &&
        props.licenseDetails?.cancellationFlagValue
      ) {
        const daysToAdd = props.licenseDetails?.monthly ? 30 : 365;
        daysLeft = daysLeft + daysToAdd;
      }
      if (daysLeft > 9) {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() + daysLeft);
        const options = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        } as const;
        return 'Until ' + currentDate.toLocaleDateString('en-US', options);
      } else if (daysLeft > 0) {
        return daysLeft + ' day' + (daysLeft > 1 ? 's' : '') + ' left';
      } else {
        return '';
      }
    };

    return (
      <Flex
        row
        align="center"
        gap={4}
        height="32px"
        {...p.uptoPhablet({ width: '100%' })}
      >
        <Flex align="center" row gap={2}>
          <StrokeIcon name="Clock" size={16} />
          <Content scale="accent" emphasis="none" lineHeight={1.25}>
            Status:
          </Content>
        </Flex>
        <Flex
          scale={
            props.licenseDetails?.status === 'trial' || wooPendingPayment
              ? 'warning'
              : props.licenseDetails?.status === 'active'
                ? 'primary'
                : 'danger'
          }
          radius="md"
          paddingBlock={1.5}
          paddingInline={3}
          align="flex-end"
          gap={1}
        >
          <Content
            scale={
              props.licenseDetails?.status === 'trial' || wooPendingPayment
                ? 'warning'
                : props.licenseDetails?.status === 'active'
                  ? 'primary'
                  : 'danger'
            }
            variant="element"
            align="center"
            size="sm"
            lineHeight={1}
            emphasis="low"
          >
            {displayStatus()}
          </Content>
        </Flex>
        <Content>
          {calculateExpiryDateStr(props.licenseDetails?.timeLeft)}
        </Content>
      </Flex>
    );
  };

  const MonthlyReportsComponent = () => {
    const redirectUrl = useMerchantSession().redirectUrl || document.referrer;
    return (
      <Flex row align="center" gap={-0.5}>
        <Content scale="accent" emphasis="none" lineHeight={1.25}>
          Monthly reports:
        </Content>
        <Button
          scale="base"
          size="md"
          variant="tertiary"
          onClick={() => {
            window.open(
              `${import.meta.env.VITE_DASHBOARD_URL}/app/widget-license/${licenseId}/remediation-report?switchAccount=${accountId}&redirect-url=${redirectUrl}`,
              '_blank',
            );
          }}
          data-mixpanel="monthly-reports"
        >
          <MixpanelButtonName>Monthly reports</MixpanelButtonName>
          <StrokeIcon name="SquareUpRightStroke" />
        </Button>
      </Flex>
    );
  };

  const accessibilityStatement = () => {
    return (
      <StatementButton
        disabled={
          props.licenseDetails?.status === 'expired' || !props.isWidgetEnabled
        }
        fullWidth={uptoPhablet}
        scale="alternative"
        variant="secondary"
        onClick={statement}
        data-mixpanel="accessibility-statement"
      >
        <StrokeIcon
          name={
            props.licenseDetails?.status === 'expired' || !props.isWidgetEnabled
              ? 'Lock'
              : 'Ribbon'
          }
        />
        <span>Accessibility Statement</span>
      </StatementButton>
    );
  };

  return (
    <BorderFlex
      radius="xl"
      padding={3}
      {...p.uptoPhablet({ padding: 4 })}
      justify="space-between"
      align="flex-start"
      self="stretch"
      gap={3}
    >
      <PlanWarningBanner />
      <Flex
        align="center"
        justify="space-between"
        width="100%"
        row
        gap={4}
        position="relative"
        boxType="border"
        {...p.fromDesktopXs({ padding: 3 })}
      >
        <Flex
          row={!isPhablet && !isMobile}
          {...p.fromDesktopXs({ height: '28px' })}
          align="center"
          gap={4}
          stretch
        >
          <PlanComponent />
          <DividerLine />
          <StatusComponent />
          {/* {!(
            props.licenseDetails?.status === 'trial' ||
            (props.licenseDetails?.status === 'expired' &&
              props.licenseDetails.plan?.name === null)
          ) && (
            <Flex
              row={!isMobile}
              align="center"
              gap={11}
              {...p.uptoDesktop({ gap: 8 })}
              {...p.uptoMobile({ gap: 4 })}
            >
              {MonthlyReportsComponent()}
            </Flex>
          )} */}
        </Flex>
        <FlyoutActionMenuContainer>
          <FlyoutActionMenu />
        </FlyoutActionMenuContainer>
      </Flex>
    </BorderFlex>
  );
};

const StyledContent = styled(Content)`
  font-size: 22px;
  font-weight: 600;
  word-break: break-all;
  @media ${fromTheme('viewport.uptoMobile')} {
    font-size: 18px;
  }
`;

const DividerLine = styled.div`
  width: 1px;
  height: 24px;
  background-color: #cbd2e1;
  @media ${fromTheme('viewport.uptoPhablet')} {
    width: 100%;
    height: 1px;
  }
`;

const FlyoutActionMenuContainer = styled.div`
  @media ${fromTheme('viewport.uptoPhablet')} {
    position: absolute;
    top: 0px;
    right: 0px;
  }
`;

const BorderFlex = styled(Flex)`
  border: 1px solid #cbd2e1;
  background-color: #fff;
`;

const StatementButton = styled(Button)`
  min-width: 200px;
`;

const MixpanelButtonName = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  z-index: -1;
`;
